import { SubscriptionHookOptions, useSubscription } from "@apollo/client";

import {
  GetChatMessagesDocument,
  GetChatMessagesQuery,
  GetChatMessagesQueryVariables,
  MessagesDocument,
  MessagesSubscription
} from "@typing/Generated";

type Message = NonNullable<MessagesSubscription["messages"]>["message"];

type UpdateFunc = (message: Message) => void;

const useSubscribeToMessages = (onUpdate?: UpdateFunc, options?: SubscriptionHookOptions) => {
  useSubscription<MessagesSubscription>(MessagesDocument, {
    ...options,
    onData: ({ client: apolloClient, data }) => {
      if (data.data?.messages?.message && onUpdate) {
        onUpdate(data.data.messages.message);
      }

      if (data.data?.messages?.message) {
        try {
          const message = data.data?.messages?.message;

          const cacheData = apolloClient.readQuery<GetChatMessagesQuery, GetChatMessagesQueryVariables>({
            query: GetChatMessagesDocument,
            variables: { id: message.chat.id }
          });

          if (!cacheData?.chatMessages.edges?.some(cachedMessage => cachedMessage?.node?.id === message.id)) {
            if (cacheData && message) {
              const newEdge = {
                __typename: "MessageEdge" as const,
                node: message
              };

              const edges = cacheData.chatMessages.edges ?? [];
              const newEdges = [...edges, newEdge];

              const newCacheData = {
                ...cacheData,
                chatMessages: {
                  ...cacheData.chatMessages,
                  edges: newEdges,
                  totalCount: cacheData.chatMessages.totalCount + 1
                }
              };

              apolloClient.writeQuery<GetChatMessagesQuery, GetChatMessagesQueryVariables>({
                data: newCacheData,
                query: GetChatMessagesDocument,
                variables: {
                  id: message.chat.id
                }
              });
            }
          }
        } catch {
          // Trying to run the query can fail
        }
      }
    },
    variables: {}
  });
};

export default useSubscribeToMessages;
